'use client';

import { useEffect, useRef } from 'react';

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

export const loadTurnstile = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(script);
  });

/**
 * Step-up widget: rendered ONLY after the server answers
 * `{ needsChallenge: true }` (the hero shortener and the changelog confirm
 * step). Forms that send email or alerts use `useHumanCheck` instead, which
 * asks for a token on every submission (website#302).
 *
 * Nothing here is in the document on first load: a visitor who never uses a
 * form never contacts Cloudflare and never receives a cookie. Do not hoist this
 * script into the layout.
 */
export default function TurnstileChallenge({
  onToken,
}: {
  onToken: (token: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  // Reachable deploy state: TURNSTILE_SECRET_KEY set server-side (so
  // turnstileConfigured() is true and the server answers needsChallenge) but
  // NEXT_PUBLIC_TURNSTILE_SITE_KEY unset. Rendering the widget in that case
  // calls window.turnstile.render with sitekey: undefined, which throws into
  // the swallowed .catch() below and leaves an empty box. Skip the widget
  // entirely and make the email fallback the primary path instead.
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return;

    let widgetId: string | undefined;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(hostRef.current, {
          sitekey: siteKey,
          callback: onToken,
          theme: 'light',
        });
      })
      .catch(() => {
        /* Swallowed: the form already shows the email fallback below. */
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken, siteKey]);

  if (!siteKey) {
    return (
      <div className="rounded-lg border border-line bg-bg-warm px-4 py-3.5">
        <p className="text-[13px] font-semibold text-ink">
          We can&apos;t verify this submission automatically right now.
        </p>
        <p className="mt-1.5 text-[13px] text-muted">
          Please email{' '}
          <a
            className="font-semibold underline underline-offset-2"
            href="mailto:support@rii.link"
          >
            support@rii.link
          </a>{' '}
          instead — we&apos;ll pick it up from there.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line bg-bg-warm px-4 py-3.5">
      <p className="mb-3 text-[13px] text-muted">
        One quick check before we send this — sorry, we get a lot of automated
        submissions.
      </p>
      <div ref={hostRef} />
      <p className="mt-2.5 text-[12px] text-dim">
        Stuck? Email us at{' '}
        <a className="underline underline-offset-2" href="mailto:support@rii.link">
          support@rii.link
        </a>
        .
      </p>
    </div>
  );
}
